import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { AxiosResponse } from 'axios'
import api from 'services/api'
import { env } from 'constants/env'
import { getUser } from 'commons/utils/user'
import { toBase64 } from 'commons/utils/object'
import {
  BindOvBody,
  CheckOvBindBody,
  OvBind,
  OVDetalheResponse,
  QueryParamsShipmentByOV,
  ReasonsJustification,
  RequestDeleteMaterial,
  RequestOvSap,
  ResponseMaterialOvActions,
  ResponseReasonsRefusal,
  ShipmentByOvResponse
} from './ovContextTypes'
import {
  setOvSap,
  getOvDetails,
  getDifferentiatedNegotiation,
  insertDifferentiatedNegotiation,
  getObservacaoOrdemVenda,
  getOvDetailedInfo,
  sendOvSap,
  checkOvBind,
  listOvBinds,
  manageOvBind
} from 'services/manage_ov'
import { toast } from 'react-toastify'
import {
  getOvStatusKey,
  sapSuccessToast,
  sendErrorToast,
  sendOVErrors
} from '../../container/ManageOVDetails/utils'
import {
  DifferentiatedNegotiationResponse,
  OvDetailedInfo,
  OvInvoice
} from 'services/manage_ov/interface'
import { OvObsMtoInterface } from 'services/manage_ov/interface'
import { getPaymentsTerms } from 'services/draft'
import {
  PaymentMethodsResponse,
  RequestParamsGetListPaymentTerms
} from 'services/draft/interfaces'
import { OVTypes, YesNo } from 'constants/enums'

type InitialState = {
  ovSelected: OVDetalheResponse
  loadingOv: boolean
  savingOv: boolean
  shipmentInfo: ShipmentByOvResponse
  invoicesInfo: OvInvoice
  shipmentIsLoading: boolean
  invoiceIsLoading: boolean
  reasonsRefusal: ResponseReasonsRefusal[]
  loadingCancelOvItem: boolean
  differentiatedNegotiation: DifferentiatedNegotiationResponse[]
  observation: OvObsMtoInterface
  reasonsJustification: ReasonsJustification[]
  paymentMethods: PaymentMethodsResponse[]
  detailedInfo: OvDetailedInfo
  loadingDetailedInfo: boolean
  materialsTabView: string
  loadingObservation: boolean
  alertJustification: boolean
  NDJustificationDescription: string
  loadingBind: boolean
  validationSuccess: string
  validationError: string
  bindOvsList: OvBind[]
  bindTotalizers: { ValorTotal: number; QuantidadeTotal: number }
  isYTRA: boolean
  isHAVAConectaOrder: boolean
}
const initialState: InitialState = {
  ovSelected: {} as OVDetalheResponse,
  loadingOv: false,
  savingOv: false,
  shipmentInfo: {} as ShipmentByOvResponse,
  invoicesInfo: {} as OvInvoice,
  shipmentIsLoading: true,
  invoiceIsLoading: true,
  reasonsRefusal: [],
  loadingCancelOvItem: false,
  differentiatedNegotiation: [],
  observation: {} as OvObsMtoInterface,
  reasonsJustification: [],
  paymentMethods: [],
  detailedInfo: {} as OvDetailedInfo,
  loadingDetailedInfo: true,
  materialsTabView: 'cor',
  loadingObservation: false,
  alertJustification: false,
  NDJustificationDescription: '',
  loadingBind: false,
  validationSuccess: '',
  validationError: '',
  bindOvsList: [],
  bindTotalizers: { ValorTotal: 0, QuantidadeTotal: 0 },
  isYTRA: false,
  isHAVAConectaOrder: false
}

const ov = createSlice({
  name: 'ov',
  initialState,
  reducers: {
    onRequest(state: InitialState) {
      state.loadingOv = true
    },
    afterRequest(state: InitialState) {
      state.loadingOv = false
    },
    isSavingOv(state: InitialState, action: PayloadAction<boolean>) {
      state.savingOv = action.payload
    },
    setOVSelected(state: InitialState, action: PayloadAction<OVDetalheResponse>) {
      state.ovSelected = action.payload
    },
    clearOVSelected(state: InitialState) {
      state.ovSelected = {} as OVDetalheResponse
    },
    setShipmentListByOvNumber(state, action: PayloadAction<ShipmentByOvResponse>) {
      state.shipmentInfo = action.payload
    },
    setInvoicesListByOvNumber(state, action: PayloadAction<OvInvoice>) {
      state.invoicesInfo = action.payload
    },
    setShipmentIsLoading(state, action: PayloadAction<boolean>) {
      state.shipmentIsLoading = action.payload
    },
    setInvoiceIsLoading(state, action: PayloadAction<boolean>) {
      state.invoiceIsLoading = action.payload
    },
    updateOvSelected(
      state,
      action: PayloadAction<Omit<OVDetalheResponse, 'Financeiro'>>
    ) {
      state.ovSelected = { ...state.ovSelected, ...action.payload }
    },
    setReasonsRefusal(state, action: PayloadAction<ResponseReasonsRefusal[]>) {
      state.reasonsRefusal = action.payload
    },
    setLoadingCancelOvItem(state, action: PayloadAction<boolean>) {
      state.loadingCancelOvItem = action.payload
    },
    setDifferentiatedNegotiation(
      state,
      action: PayloadAction<DifferentiatedNegotiationResponse[]>
    ) {
      state.differentiatedNegotiation = action.payload
    },
    setObservation(state, action: PayloadAction<OvObsMtoInterface>) {
      state.observation = action.payload
    },
    setNDJustificationDescription(state, action: PayloadAction<string>) {
      state.NDJustificationDescription = action.payload
    },
    setReasonsJustification(state, action: PayloadAction<ReasonsJustification[]>) {
      state.reasonsJustification = action.payload
    },
    setPaymentMethods(state, action: PayloadAction<PaymentMethodsResponse[]>) {
      state.paymentMethods = action.payload
    },
    setOvDetailedInfo(state, action: PayloadAction<OvDetailedInfo>) {
      state.detailedInfo = action.payload
    },
    setLoadingDetailedInfo(state, action: PayloadAction<boolean>) {
      state.loadingDetailedInfo = action.payload
    },
    setMaterialsTabView(state, action: PayloadAction<string>) {
      state.materialsTabView = action.payload
    },
    setLoadingObservation(state, action: PayloadAction<boolean>) {
      state.loadingObservation = action.payload
    },
    setAlertJustification(state, action: PayloadAction<boolean>) {
      state.alertJustification = action.payload
    },
    setBindLoading(state, action: PayloadAction<boolean>) {
      state.loadingBind = action.payload
    },
    setBindValidationStatus(state, action: PayloadAction<string>) {
      state.validationSuccess = action.payload === 'success' ? 'OV disponível' : ''
      state.validationError = action.payload === 'error' ? 'OV indisponível' : ''
    },
    resetBindValidationStatus(state) {
      state.validationSuccess = ''
      state.validationError = ''
    },
    setBindOvsList(state, action: PayloadAction<OvBind[]>) {
      state.bindOvsList = action.payload
    },
    setBindTotalizers(
      state,
      action: PayloadAction<{ ValorTotal: number; QuantidadeTotal: number }>
    ) {
      state.bindTotalizers = action.payload
    },
    setIsYTRA(state, action: PayloadAction<boolean>) {
      state.isYTRA = action.payload
    },
    setIsHAVAConectaOrder(state, action: PayloadAction<boolean>) {
      state.isHAVAConectaOrder = action.payload
    }
  }
})

export const ovContext = ov.reducer
export const {
  afterRequest,
  clearOVSelected,
  onRequest,
  isSavingOv,
  setOVSelected,
  setShipmentListByOvNumber,
  setShipmentIsLoading,
  updateOvSelected,
  setReasonsRefusal,
  setLoadingCancelOvItem,
  setInvoiceIsLoading,
  setDifferentiatedNegotiation,
  setInvoicesListByOvNumber,
  setObservation,
  setReasonsJustification,
  setPaymentMethods,
  setOvDetailedInfo,
  setLoadingDetailedInfo,
  setMaterialsTabView,
  setLoadingObservation,
  setAlertJustification,
  setNDJustificationDescription,
  setBindLoading,
  setBindValidationStatus,
  resetBindValidationStatus,
  setBindOvsList,
  setBindTotalizers,
  setIsYTRA,
  setIsHAVAConectaOrder
} = ov.actions

export function asyncGetOVById(ovNumber: string): AppThunk {
  return async function (dispatch) {
    dispatch(onRequest())

    api
      .get(`${env.manageOVBasePath}/${ovNumber}/${env.ovDetailBasePath}`)
      .then(async (response: AxiosResponse<OVDetalheResponse>) => {
        // const clientId = response.data.Cabecalho.CodigoSapCliente
        // // const financial = await api
        // //   .get(
        // //     `${env.clientBasePath}/${env.apiVersion}/${env.creditByClientIdBasePath}?cd_clie=${clientId}`
        // //   )
        // //   .then((res: AxiosResponse<Financeiro>) => {
        // //     return res.data
        // //   })

        const obsResponse = await getObservacaoOrdemVenda(ovNumber)
        dispatch(setObservation(obsResponse?.data))

        dispatch(
          setOVSelected({
            ...response.data,
            statusOrdemVenda: getOvStatusKey(+response.data.Cabecalho.CodigoStatus)
          })
        )
        dispatch(setIsYTRA(response.data.Cabecalho.CodigoTipoOrdem === OVTypes.YTRA))
        dispatch(getND(ovNumber))
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => dispatch(afterRequest()))
  }
}

export const asyncGetShipmentListByOvNumber =
  (ovNumber: string, query: QueryParamsShipmentByOV): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setShipmentIsLoading(true))
      const { currentPage, pageSize, searchValue } = query
      const viewParam = query.selectedView === 'sku' ? '-sku' : ''
      const response = await api.get<ShipmentByOvResponse>(
        `${env.manageOVBasePath}/${ovNumber}/remessas${viewParam}?paginas=${currentPage}&linhas=${pageSize}&material=${searchValue}`
      )
      dispatch(setShipmentListByOvNumber(response.data))
    } catch (error) {
      toast.error('Erro ao obter lista de remessas')
    } finally {
      dispatch(setShipmentIsLoading(false))
    }
  }
export const asyncGetInvoicesListByOvNumber =
  (type: 'doc' | 'sku', ovNumber: string, query: QueryParamsShipmentByOV): AppThunk =>
  async (dispatch) => {
    const text = type === 'doc' ? 'faturamento' : 'faturamento-sku'
    try {
      dispatch(setInvoiceIsLoading(true))
      const { currentPage, pageSize, searchValue } = query
      const response = await api.get<OvInvoice>(
        `${env.manageOVBasePath}/${ovNumber}/${text}?paginas=${currentPage}&linhas=${pageSize}&material=${searchValue}`
      )
      dispatch(setInvoicesListByOvNumber(response.data))
    } catch (error) {
      toast.error('Erro ao obter lista de faturamento')
    } finally {
      dispatch(setInvoiceIsLoading(false))
    }
  }

export const asyncSetOvSap =
  ({ type, body, reload }: RequestOvSap): AppThunk =>
  async (dispatch) => {
    try {
      if (body) {
        if (reload) {
          dispatch(onRequest())
        }
        const requestType = type === 'devolver' ? 'retornar' : type

        const sendBody = body?.map((ov) => ({
          NumeroOrdemVenda: ov.numeroOrdemVenda,
          DataAlteracao: ov.dataAlteracao
        }))

        const ovToSapResponse =
          type === 'enviar' && sendBody !== undefined
            ? await sendOvSap({ ListaOvs: sendBody })
            : await setOvSap({ body, type: requestType })

        const { MensagemRetorno } = ovToSapResponse.data[0]

        if (ovToSapResponse?.data[0]?.OperacaoRealizada) {
          const ovDetailsResponse = await getOvDetails(body[0].numeroOrdemVenda)

          dispatch(
            updateOvSelected({
              ...ovDetailsResponse.data,
              statusOrdemVenda: getOvStatusKey(
                ovDetailsResponse?.data.Cabecalho.CodigoStatus
              )
            })
          )

          sapSuccessToast(type)
        } else {
          if (Object.values(sendOVErrors).includes(MensagemRetorno)) {
            sendErrorToast(MensagemRetorno)
            dispatch(setAlertJustification(true))
          } else {
            toast.error(MensagemRetorno)
          }
        }
      }
    } catch (error) {
      toast.error(
        `Não foi possível ${type} a OV devido a um erro. Tente novamente mais tarde ou entre em contato com o time de Backoffice.`
      )
    } finally {
      dispatch(afterRequest())
      if (body) {
        dispatch(asyncGetOVById(body[0].numeroOrdemVenda))
        dispatch(getDetailedInfo(body[0].numeroOrdemVenda))
      }
    }
  }

export const ovItemActions =
  (
    requestType: 'cancelar' | 'excluir',
    ovNumber: string,
    requestBody: RequestDeleteMaterial[]
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(onRequest())
      const toastMsg =
        requestBody.length > 1
          ? `Registros
            ${requestType === 'excluir' ? 'excluídos' : ''}
            ${requestType === 'cancelar' ? 'cancelados' : ''} com sucesso.`
          : `Registro
            ${requestType === 'excluir' ? 'excluído' : ''}
            ${requestType === 'cancelar' ? 'cancelado' : ''} com sucesso.`

      const formattedRequestBody: RequestDeleteMaterial[] = []
      const itemsNumbers: number[] = []
      requestBody.forEach((item) => {
        if (!itemsNumbers.includes(item.numeroItemOrdemVenda)) {
          itemsNumbers.push(item.numeroItemOrdemVenda)
          formattedRequestBody.push(item)
        }
      })

      const response = await api.post<ResponseMaterialOvActions[]>(
        `gerir-ov/${ovNumber}/${requestType}-item`,
        formattedRequestBody
      )
      dispatch(getND(ovNumber))
      if (response.data.every((item) => item.OperacaoRealizada)) {
        dispatch(asyncGetOVById(ovNumber))
        toast.success(toastMsg)
      } else if (response.data.some((item) => item.OperacaoRealizada)) {
        dispatch(asyncGetOVById(ovNumber))
        toast.warning(`Não foi possível ${requestType} alguns dos itens selecionados.`)
      } else {
        toast.error(`Não foi possível ${requestType} os itens selecionados.`)
      }
    } catch (error) {
      toast.error(`Erro ao ${requestType} o item.`)
    } finally {
      dispatch(afterRequest())
    }
  }

export const getReasonsRefusal = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setLoadingCancelOvItem(true))
    const response = await api.get<ResponseReasonsRefusal[]>(`gerir-ov/motivo-recusa`)
    dispatch(setReasonsRefusal(response.data))
  } catch (error) {
    toast.error(`Erro ao carregar motivos de recusa`)
  } finally {
    dispatch(setLoadingCancelOvItem(false))
  }
}

export const saveOvFields =
  (
    file: File,
    file_name: string,
    reasonRequest: string,
    codReason: string,
    requestDescription?: string
  ): AppThunk =>
  async (dispatch, getState) => {
    dispatch(isSavingOv(true))

    const { NumeroOrdemVenda } = getState().ov.differentiatedNegotiation[0]

    const arquivo = file ? ((await toBase64(file)) as string) : ''

    const requestBody = {
      NumeroOrdemVenda,
      CodigoMotivoNegociacao: codReason,
      NegociacaoDiferenciada: reasonRequest,
      JustificativaNegociacaoDiferenciada: requestDescription,
      NomeAnexo: file_name,
      Anexo: arquivo ? arquivo.split(',')[1] : null,
      CodigoUsuarioAlteracao: getUser()
    }

    try {
      await insertDifferentiatedNegotiation(requestBody)
      dispatch(getObservationInfo(NumeroOrdemVenda))
      dispatch(getDetailedInfo(NumeroOrdemVenda))

      toast.success(`Ordem de venda salva com sucesso.`)
      dispatch(setAlertJustification(false))
    } catch (error) {
      toast.error(`Erro ao salvar ordem de venda.`)
    } finally {
      dispatch(isSavingOv(false))
    }
  }

export const getND =
  (ovNumber: string): AppThunk =>
  async (dispatch, getState) => {
    const currentNegDiff = getState().ov.differentiatedNegotiation[0]
    try {
      const differentiatedNegotiation = await getDifferentiatedNegotiation(ovNumber)
      const { NegociacaoDiferenciada: newNegDiff } = differentiatedNegotiation[0]
      if (
        currentNegDiff &&
        currentNegDiff.NegociacaoDiferenciada !== newNegDiff &&
        newNegDiff === YesNo.YES
      ) {
        toast.warning(
          'O ajuste feito tornou esta Ordem de Venda em uma Negociação Diferenciada!'
        )
      }

      dispatch(setDifferentiatedNegotiation(differentiatedNegotiation))
      dispatch(
        setNDJustificationDescription(
          differentiatedNegotiation[0].JustificativaNegociacaoDiferenciada || ''
        )
      )
    } catch (error) {
      toast.error(`Erro ao carregar negociação diferenciada`)
    }
  }

export const getReasonsJustification = (): AppThunk => async (dispatch) => {
  try {
    const response = await api.get<ReasonsJustification[]>(
      `${env.manageOVBasePath}/motivos-negociacao-diferenciada`
    )
    dispatch(setReasonsJustification(response.data))
  } catch (error) {
    toast.error(`Erro ao carregar motivos de justificativa`)
  }
}

export const getPaymentMethods = (): AppThunk => async (dispatch) => {
  const params: RequestParamsGetListPaymentTerms = {
    qt_rowcount: 999,
    cd_empr: '',
    cd_orgn_vend: '',
    cd_canl_dstb: '',
    cd_tipo_orde: '',
    fl_orde_tele_vend: YesNo.NO,
    cd_cond_pgto: ''
  }
  try {
    const paymentMethods = await getPaymentsTerms(params)
    dispatch(setPaymentMethods(paymentMethods))
  } catch (error) {
    toast.error(`Erro ao carregar as condições de pagamento`)
  }
}

export const getDetailedInfo =
  (ovNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingDetailedInfo(true))
    try {
      const ovDetailedInfo = await getOvDetailedInfo(ovNumber)
      dispatch(setOvDetailedInfo(ovDetailedInfo))
    } catch (error) {
      toast.error(`Erro ao carregar detalhes da OV`)
    } finally {
      dispatch(setLoadingDetailedInfo(false))
    }
  }

export const getObservationInfo =
  (ovNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingObservation(true))
    try {
      const observation = await getObservacaoOrdemVenda(ovNumber)
      dispatch(setObservation(observation.data))
    } catch (error) {
      toast.error(`Erro ao carregar textos da OV`)
    } finally {
      dispatch(setLoadingObservation(false))
    }
  }

export const checkOVBindStatus =
  (body: CheckOvBindBody): AppThunk =>
  async (dispatch) => {
    dispatch(setBindLoading(true))
    try {
      const bindCondition = await checkOvBind(body)
      if (bindCondition) dispatch(setBindValidationStatus('success'))
      else dispatch(setBindValidationStatus('error'))
    } catch {
      toast.error(
        'Não foi possível verificar essa OV pois houve uma instabilidade no sistema. Tente novamente mais tarde.'
      )
    } finally {
      dispatch(setBindLoading(false))
    }
  }

export const getOvBinds =
  (ovNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setBindLoading(true))
    try {
      const response = await listOvBinds(ovNumber)
      if (response.ListaOV?.length) {
        dispatch(setBindOvsList(response.ListaOV))
        dispatch(
          setBindTotalizers({
            ValorTotal: response.ValorTotalItem,
            QuantidadeTotal: response.QuantidadeTotalItem
          })
        )
      } else {
        dispatch(setBindOvsList([]))
        dispatch(setBindTotalizers({ ValorTotal: 0, QuantidadeTotal: 0 }))
      }
    } catch (error) {
      toast.error(
        `Não foi possível carregar OVs vinculadas pois houve uma instabilidade no sistema. Tente novamente mais tarde.`
      )
    } finally {
      dispatch(setBindLoading(false))
    }
  }

export const ovBindOperation =
  (body: BindOvBody): AppThunk =>
  async (dispatch) => {
    dispatch(setBindLoading(true))
    try {
      const response = await manageOvBind(body)

      if (response.OperacaoRealizada) {
        toast.success(
          `OV YPEM ${
            body.tipoOperacao === 'I' ? 'vinculada' : 'desvinculada'
          } com sucesso.`
        )
        dispatch(getOvBinds(body.numeroordemvenda))
      } else {
        toast.error(
          `Não foi possível realizar a operação. Verifique o número da OV e tente novamente.`
        )
      }
    } catch (error) {
      toast.error(
        `Não foi possível vincular essa OV YPEM pois houve uma instabilidade no sistema. Tente novamente mais tarde.`
      )
    } finally {
      dispatch(setBindLoading(false))
    }
  }
